import { useMemo, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { useGameStore } from "../../store";
import { Lighting } from "./Lighting";
import { Track } from "./Track";
import { Decor } from "./Decor";
import { PlayerCharacter } from "./PlayerCharacter";
import { SideCamera } from "./SideCamera";
import { Confetti } from "./Confetti";
import { Podium, usePodium } from "./Podium";
import { QuestionBubbles } from "./QuestionBubbles";
import {
  avatarFileForPlayer,
  UNITS_PER_STEP,
  LANE_SPACING,
  PODIUM_STAGE_X,
  PODIUM_FLASH_IN_MS,
  PODIUM_FLASH_HOLD_MS,
  PODIUM_FLASH_OUT_MS,
} from "./constants";

// Plancher de slots : pour un set court (≤ ~12 questions), garde 120 pour que
// la piste paraisse "longue" même si le joueur s'arrête vite.
const MIN_TRACK_SLOTS = 120;

// Max théorique de steps par question = 10 (cf. Quiz.tsx, courbe quadratique 1-10).
// Triplé à 30 pour avoir une marge de sécurité confortable (cas où la courbe ou
// le scoring évoluent, ou pour absorber les coups de chance enchaînés).
// Buffer fixe de 20 en plus pour que la flèche d'arrivée reste visible derrière.
const STEPS_PER_QUESTION_MAX = 30;
const TRACK_END_BUFFER = 20;

export function ThreeGame() {
  const { players, finalPlayers, socket, isSpectator, roomHostId, gameCompleted, finishedPlayers, questions } = useGameStore();

  // Dès que la course est finie, on bascule sur le classement figé par le
  // serveur. Tout le reste de ce composant en découle (rangs, places du podium,
  // cadrage caméra, confettis), donc c'est le seul point à geler : sans ça, un
  // joueur qui ferme son téléphone sur le podium voyait son avatar disparaître
  // et tous les rangs derrière lui remonter d'un cran.
  const rosterPlayers = finalPlayers ?? players;

  // Podium optionnel : null tant que /bg/Podium.glb n'existe pas (cf. usePodium).
  const podium = usePodium();

  // Dimensionner la piste sur le nombre réel de questions du set en cours, sinon
  // les sets longs (≥ 20 questions) sortent du décor et la piste/track s'arrête
  // avant le joueur. Plafonné par MIN_TRACK_SLOTS pour les sets courts.
  const trackSlots = useMemo(
    () => Math.max(MIN_TRACK_SLOTS, questions.length * STEPS_PER_QUESTION_MAX + TRACK_END_BUFFER),
    [questions.length]
  );

  // Filtrer le host — le host spectateur n'est pas visible sur la piste
  const visiblePlayers = useMemo(
    () => rosterPlayers.filter((p) => p.id !== roomHostId),
    [rosterPlayers, roomHostId]
  );

  // Rankings
  const rankMap = useMemo(() => {
    const sorted = [...visiblePlayers].sort((a, b) => b.score - a.score);
    return new Map(sorted.map((p, i) => [p.id, i + 1]));
  }, [visiblePlayers]);

  // Winner = joueur avec le meilleur score
  const winnerId = useMemo(() => {
    if (!gameCompleted || visiblePlayers.length === 0) return null;
    const sorted = [...visiblePlayers].sort((a, b) => b.score - a.score);
    return sorted[0].id;
  }, [gameCompleted, visiblePlayers]);

  // --- Séquence de fin : flash blanc puis révélation du podium ---
  // flash   : overlay blanc CSS au-dessus du canvas
  // onPodium: les avatars et la caméra sont passés sur le podium
  // La bascule onPodium se fait PENDANT le plateau blanc, donc la téléportation
  // n'est jamais visible : on voit la course, un flash, puis le podium monté.
  const [flash, setFlash] = useState(false);
  const [onPodium, setOnPodium] = useState(false);

  useEffect(() => {
    if (!gameCompleted || !podium) return;

    setFlash(true);
    const toPodium = setTimeout(() => setOnPodium(true), PODIUM_FLASH_IN_MS);
    const fadeOut = setTimeout(
      () => setFlash(false),
      PODIUM_FLASH_IN_MS + PODIUM_FLASH_HOLD_MS
    );
    return () => {
      clearTimeout(toPodium);
      clearTimeout(fadeOut);
    };
  }, [gameCompleted, podium]);

  // Cible de chaque avatar sur le podium : rang N -> place_N. Un rang sans spot
  // (plus de joueurs que de places dans le GLB) n'est pas téléporté, il finit la
  // partie là où il s'est arrêté plutôt que de s'empiler sur une place occupée.
  const podiumTargets = useMemo(() => {
    const map = new Map<string, [number, number, number]>();
    if (!podium || !onPodium) return map;
    for (const p of visiblePlayers) {
      const rank = rankMap.get(p.id);
      const offset = rank ? podium.spots.get(rank) : undefined;
      if (!offset) continue;
      map.set(p.id, [PODIUM_STAGE_X + offset.x, offset.y, offset.z]);
    }
    return map;
  }, [podium, onPodium, visiblePlayers, rankMap]);

  // Places réellement occupées, pour que la caméra cadre les joueurs présents et
  // pas les 10 emplacements du GLB (sur une partie à 2, cadrer les 10 reculerait
  // la caméra au point de rendre les avatars minuscules).
  const occupiedSpots = useMemo(
    () => [...podiumTargets.values()],
    [podiumTargets]
  );

  // Position des confettis : sur la place du vainqueur une fois le podium monté,
  // sinon à l'ancienne (au-dessus de l'avatar gagnant sur la piste).
  const winnerPlayer = winnerId ? visiblePlayers.find((p) => p.id === winnerId) : null;
  const winnerIndex = winnerId ? visiblePlayers.findIndex((p) => p.id === winnerId) : -1;
  const lanesOffset = ((visiblePlayers.length - 1) * LANE_SPACING) / 2;
  const winnerZ = winnerIndex >= 0 ? -winnerIndex * LANE_SPACING + lanesOffset : 0;

  // Une bulle de question porte deja le nom du joueur : afficher en plus la
  // pastille au-dessus de l'avatar ferait doublon. On ne la garde donc que quand
  // il n'y a PAS de bulle (joueur qui a fini, podium, fin de partie), sinon le nom
  // disparaitrait completement.
  const bubblesVisible = isSpectator && !gameCompleted && questions.length > 0;

  const confettiPosition = useMemo((): [number, number, number] => {
    const podiumSpot = winnerId ? podiumTargets.get(winnerId) : undefined;
    if (podiumSpot) return [podiumSpot[0], podiumSpot[1] + 3, podiumSpot[2]];
    return [(winnerPlayer?.position || 0) * UNITS_PER_STEP, 3, winnerZ];
  }, [winnerId, podiumTargets, winnerPlayer, winnerZ]);

  return (
    <div className="absolute inset-0">
      <Canvas
        shadows
        camera={{ fov: 50, near: 0.1, far: 200 }}
        style={{ background: "#87CEEB" }}
      >
        <SideCamera
          players={visiblePlayers}
          currentPlayerId={socket?.id}
          isSpectator={isSpectator}
          gameCompleted={gameCompleted}
          winnerId={winnerId}
          winnerZ={winnerZ}
          onPodium={onPodium}
          podiumView={podium?.view ?? null}
          podiumSpots={occupiedSpots}
        />
        <Lighting />

        {/* Piste et décor démontés dès qu'on bascule sur le podium : le podium est
            un diorama complet posé très loin sur X, garder la piste montée ne sert
            qu'à consommer du GPU pour de la géométrie hors champ. */}
        {!onPodium && (
          <>
            <Track slotCount={trackSlots} laneCount={visiblePlayers.length} />
            <Decor trackSlots={trackSlots} />
          </>
        )}

        {/* Podium : monté seulement en fin de partie, et seulement si le GLB existe */}
        {podium && onPodium && (
          <Podium data={podium} position={[PODIUM_STAGE_X, 0, 0]} />
        )}

        {visiblePlayers.map((player, index) => (
          <PlayerCharacter
            key={player.id}
            player={player}
            laneIndex={index}
            totalLanes={visiblePlayers.length}
            rank={rankMap.get(player.id) || index + 1}
            isCurrentPlayer={player.id === socket?.id}
            avatarFile={avatarFileForPlayer(player.id)}
            isWinner={gameCompleted && player.id === winnerId}
            isFinished={finishedPlayers.has(player.id)}
            showLabel={isSpectator && !(bubblesVisible && !finishedPlayers.has(player.id))}
            podiumTarget={podiumTargets.get(player.id) ?? null}
            onPodium={onPodium}
          />
        ))}

        {/* Bulles de question : ecran spectateur uniquement, pendant la course.
            Sur le telephone du joueur elles n'ont aucun sens (il a deja sa question
            en plein ecran) et masqueraient la piste. */}
        {bubblesVisible && (
          <QuestionBubbles
            players={visiblePlayers}
            questions={questions}
            finishedPlayers={finishedPlayers}
          />
        )}

        {/* Confettis autour du winner */}
        {gameCompleted && winnerPlayer && (
          <Confetti
            position={confettiPosition}
            size={onPodium ? 7 : 1}
            spread={onPodium ? 2 : 1}
          />
        )}
      </Canvas>

      {/* Flash blanc de transition course -> podium. Hors du Canvas : un simple
          overlay CSS est plus net et moins coûteux qu'un plane fullscreen en 3D.
          La durée change selon le sens (montée courte, révélation plus lente). */}
      <div
        className="absolute inset-0 bg-white pointer-events-none"
        style={{
          opacity: flash ? 1 : 0,
          transition: `opacity ${flash ? PODIUM_FLASH_IN_MS : PODIUM_FLASH_OUT_MS}ms ease-out`,
        }}
      />
    </div>
  );
}
